import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { API_BASE_IOTHUB, authHeaders } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { useStatusMessage } from '../hooks/useStatusMessage';
import { SearchDropdown, TooltipIcon, StatusBar } from '../UI-Elements';
import ErrorBoundary from './ErrorBoundary';
import { Zurück, Downlink, Abbrechen } from '../icons';
import './SendDownlink.css';

const APPLY_TO_OTHERS_OPTIONS = [
  { value: 'YES', label: 'Ja' },
  { value: 'NO', label: 'Nein' },
];

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Aktivieren' },
  { value: 'false', label: 'Deaktivieren' },
];

function SectionHeader({ name, number, title, tooltip, required }) {
  return (
    <div className="downlink-field-label">
      <span className="downlink-field-number" aria-hidden="true">{number}</span>
      <span id={`downlink-label-${name}`}>{title}{required && <span className="downlink-required">*</span>}</span>
      {tooltip && <TooltipIcon text={tooltip} idSuffix={`downlink-${name}`} />}
    </div>
  );
}

function FieldError({ children }) {
  return <div className="downlink-error-text" role="alert">{children}</div>;
}

function RadioGroup({ name, labelledBy, options, value, onChange }) {
  return (
    <div className="downlink-radio-group" role="radiogroup" aria-labelledby={labelledBy}>
      {options.map(o => (
        <label key={o.value} className="downlink-radio-label">
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

// Validiert einen Parameterwert gegen sein JSON-Schema und liefert die
// Fehlermeldung bzw. '' bei gültigem Wert.
function validateParameter(schema, value, required) {
  if (value === '' || value === undefined) {
    return required ? 'Dieses Feld ist erforderlich.' : '';
  }
  if (schema.type === 'integer') {
    if (!/^-?\d+$/.test(value)) return 'Bitte eine ganze Zahl eingeben.';
    const num = Number(value);
    const hasMin = typeof schema.minimum === 'number';
    const hasMax = typeof schema.maximum === 'number';
    if ((hasMin && num < schema.minimum) || (hasMax && num > schema.maximum)) {
      if (hasMin && hasMax) return `Bitte einen Wert zwischen ${schema.minimum} und ${schema.maximum} eingeben.`;
      if (hasMin) return `Bitte einen Wert größer oder gleich ${schema.minimum} eingeben.`;
      return `Bitte einen Wert kleiner oder gleich ${schema.maximum} eingeben.`;
    }
  }
  return '';
}

function toPayloadValue(schema, value) {
  if (schema.type === 'integer') return Number(value);
  if (schema.type === 'boolean') return value === 'true';
  return value;
}

// Der Bulk-Endpunkt nimmt höchstens 100 Geräte je Anfrage an
// (inklusive des Ursprungsgeräts).
const MAX_BULK_DEVICES = 100;
const DEVICE_PAGE_SIZE = 1000;

// Lädt alle Seiten von GET /api/v1/devices.
async function fetchAllDevices(token, signal) {
  const devices = [];
  for (let page = 0; ; page++) {
    const res = await fetch(
      `${API_BASE_IOTHUB}/api/v1/devices?page=${page}&page_size=${DEVICE_PAGE_SIZE}`,
      { headers: authHeaders(token), signal }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const pageDevices = data.devices || [];
    devices.push(...pageDevices);
    if (pageDevices.length < DEVICE_PAGE_SIZE || devices.length >= (data.total ?? 0)) return devices;
  }
}

function SendDownlink() {
  const { iotHubToken } = useAuth();
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const location = useLocation();
  const { statusMessage, statusType, showStatus } = useStatusMessage();

  const [device, setDevice] = useState(() => (
    location.state?.name ? { name: location.state.name } : null
  ));
  const [commands, setCommands] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [selectedCommand, setSelectedCommand] = useState('');
  const [commandText, setCommandText] = useState('');
  const [paramValues, setParamValues] = useState({});

  const [applyToOthers, setApplyToOthers] = useState('NO');
  const [otherDevices, setOtherDevices] = useState([]);
  const [otherDevicesLoading, setOtherDevicesLoading] = useState(false);
  const [otherDevicesError, setOtherDevicesError] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [deviceText, setDeviceText] = useState('');
  const [allDevicesSelected, setAllDevicesSelected] = useState(false);

  const [showErrors, setShowErrors] = useState(false);
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const touch = (field) => setTouched(prev => (prev[field] ? prev : { ...prev, [field]: true }));

  // Gerätedetails (Name, Hersteller, Modell) und verfügbare Commands laden
  useEffect(() => {
    if (!deviceId || !iotHubToken) return;
    const controller = new AbortController();
    async function load() {
      try {
        setLoading(true);
        setLoadError('');
        const headers = authHeaders(iotHubToken);
        const [deviceRes, downlinksRes] = await Promise.all([
          fetch(`${API_BASE_IOTHUB}/api/v1/devices/${deviceId}`, { headers, signal: controller.signal }),
          fetch(`${API_BASE_IOTHUB}/api/v1/devices/${deviceId}/downlinks`, { headers, signal: controller.signal }),
        ]);
        if (!deviceRes.ok) throw new Error(`Gerätedetails konnten nicht geladen werden (HTTP ${deviceRes.status})`);
        if (!downlinksRes.ok) {
          const errData = await downlinksRes.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Downlink-Befehle konnten nicht geladen werden (HTTP ${downlinksRes.status})`);
        }
        const deviceData = await deviceRes.json();
        const downlinkData = await downlinksRes.json();
        setDevice({
          name: deviceData.name,
          vendor: deviceData.details?.vendor,
          model: deviceData.details?.model,
        });
        setCommands(downlinkData?.commands || {});
      } catch (err) {
        if (err.name === 'AbortError') return;
        setLoadError(err.message || 'Netzwerkfehler. Bitte erneut versuchen.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [deviceId, iotHubToken]);

  // Weitere Geräte desselben Herstellers und Modells erst laden, wenn sie gebraucht werden
  useEffect(() => {
    if (applyToOthers !== 'YES' || !iotHubToken || !device?.vendor || !device?.model) return;
    const controller = new AbortController();
    async function loadDevices() {
      try {
        setOtherDevicesLoading(true);
        setOtherDevicesError('');
        const devices = await fetchAllDevices(iotHubToken, controller.signal);
        setOtherDevices(
          devices
            .map(d => ({
              id: d.bff_device_id ?? d.tb_device_id,
              name: d.name,
              vendor: d.details?.vendor,
              model: d.details?.model,
            }))
            .filter(d => d.id && d.id !== deviceId && d.vendor === device.vendor && d.model === device.model)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        );
      } catch (err) {
        if (err.name === 'AbortError') return;
        setOtherDevicesError(`Weitere Geräte konnten nicht geladen werden: ${err.message || 'Netzwerkfehler'}.`);
      } finally {
        if (!controller.signal.aborted) setOtherDevicesLoading(false);
      }
    }
    loadDevices();
    return () => controller.abort();
  }, [applyToOthers, iotHubToken, deviceId, device?.vendor, device?.model]);

  const commandOptions = useMemo(
    () => Object.keys(commands).map(c => ({ value: c, label: c })),
    [commands]
  );

  const commandDef = selectedCommand ? commands[selectedCommand] : null;
  const paramProperties = commandDef?.parameters?.properties || {};
  const requiredParams = commandDef?.parameters?.required || [];
  const paramEntries = Object.entries(paramProperties);

  const paramErrors = {};
  for (const [key, schema] of paramEntries) {
    const msg = validateParameter(schema, paramValues[key] ?? '', requiredParams.includes(key));
    if (msg) paramErrors[key] = msg;
  }

  const deviceOptions = useMemo(
    () => otherDevices
      .filter(d => !selectedDevices.some(s => s.id === d.id))
      .map(d => ({ value: d.id, label: d.name || d.id })),
    [otherDevices, selectedDevices]
  );

  const targetDevices = applyToOthers !== 'YES'
    ? []
    : allDevicesSelected ? otherDevices : selectedDevices;

  const showError = (field) => showErrors || !!touched[field];
  const commandError = showError('command') && !selectedCommand;
  const devicesError = showErrors && applyToOthers === 'YES' && targetDevices.length === 0;
  const tooManyDevices = targetDevices.length + 1 > MAX_BULK_DEVICES;
  const isValid = !!selectedCommand && Object.keys(paramErrors).length === 0 &&
    (applyToOthers !== 'YES' || targetDevices.length > 0) && !tooManyDevices;

  const selectCommand = (command) => {
    setSelectedCommand(command);
    setParamValues({});
    setTouched(prev => ({ command: prev.command }));
    setSubmitError('');
  };

  const changeApplyToOthers = (value) => {
    setApplyToOthers(value);
    if (value === 'NO') {
      setSelectedDevices([]);
      setDeviceText('');
      setAllDevicesSelected(false);
    }
  };

  const resetForm = () => {
    selectCommand('');
    setCommandText('');
    changeApplyToOthers('NO');
    setTouched({});
  };

  const handleAllDevices = (checked) => {
    setAllDevicesSelected(checked);
    if (!checked) setSelectedDevices([]);
    setDeviceText('');
  };

  const removeDevice = (id) => {
    setSelectedDevices(prev => prev.filter(d => d.id !== id));
    setAllDevicesSelected(false);
  };

  // Enter in einem Eingabefeld soll das Formular nicht abschicken, sondern
  // zum nächsten Feld springen. Abgeschickt wird nur über den Button selbst.
  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter' || e.target.tagName !== 'INPUT' || e.defaultPrevented) return;
    e.preventDefault();
    if (e.target.getAttribute('role') === 'combobox') return;
    const fields = Array.from(e.currentTarget.querySelectorAll(
      'input:not([disabled]):not([type="radio"]), input[type="radio"]:checked:not([disabled])'
    ));
    fields[fields.indexOf(e.target) + 1]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);

    const parameters = {};
    for (const [key, schema] of paramEntries) {
      const value = paramValues[key];
      if (value !== undefined && value !== '') parameters[key] = toPayloadValue(schema, value);
    }

    const deviceIds = [deviceId, ...targetDevices.map(d => d.id)];
    const isBulk = deviceIds.length > 1;

    try {
      setSending(true);
      const res = await fetch(
        isBulk
          ? `${API_BASE_IOTHUB}/api/v1/devices/downlinks/bulk`
          : `${API_BASE_IOTHUB}/api/v1/devices/${deviceId}/downlinks`,
        {
          method: 'POST',
          headers: authHeaders(iotHubToken),
          body: JSON.stringify(isBulk
            ? { command: selectedCommand, parameters, bff_device_ids: deviceIds }
            : { command: selectedCommand, parameters }),
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        const detail = typeof errData?.detail === 'string' ? errData.detail : errData?.message;
        throw new Error(detail || `HTTP ${res.status}`);
      }
      if (!isBulk) {
        showStatus(`Downlink „${selectedCommand}" wurde für „${device?.name}" in die Warteschlange eingereiht.`);
        resetForm();
        return;
      }
      // Bulk: HTTP 200 heißt nur, dass die Anfrage verarbeitet wurde –
      // einzelne Geräte können trotzdem gescheitert sein.
      const result = await res.json().catch(() => null);
      const failed = result?.failed || [];
      if (failed.length > 0) {
        const names = new Map([[deviceId, device?.name], ...targetDevices.map(d => [d.id, d.name])]);
        const failedList = failed.map(f => `${names.get(f.bff_device_id) || f.bff_device_id} (${f.detail})`).join(', ');
        setSubmitError(`Downlink wurde für ${result.successful_count} von ${deviceIds.length} Geräten eingereiht. Fehlgeschlagen: ${failedList}.`);
        return;
      }
      showStatus(`Downlink „${selectedCommand}" wurde für ${deviceIds.length} Geräte in die Warteschlange eingereiht.`);
      resetForm();
    } catch (err) {
      setSubmitError(`Downlink konnte nicht gesendet werden: ${err.message || 'Netzwerkfehler'}.`);
    } finally {
      setSending(false);
    }
  };

  const renderParameterInput = (key, schema) => {
    const value = paramValues[key] ?? '';
    const error = showError(`param-${key}`) && paramErrors[key];
    const setValue = (v) => setParamValues(prev => ({ ...prev, [key]: v }));

    if (schema.type === 'boolean') {
      return (
        <div key={key} className="downlink-param">
          <p className="downlink-param-name" id={`downlink-param-label-${key}`}>{key}</p>
          <RadioGroup
            name={`param-${key}`}
            labelledBy={`downlink-param-label-${key}`}
            options={BOOLEAN_OPTIONS}
            value={value}
            onChange={v => { setValue(v); touch(`param-${key}`); }}
          />
          {error && <FieldError>{paramErrors[key]}</FieldError>}
        </div>
      );
    }

    const isInteger = schema.type === 'integer';
    const hasRange = typeof schema.minimum === 'number' && typeof schema.maximum === 'number';
    return (
      <div key={key} className="downlink-param">
        <div className="downlink-input-wrapper">
          <label className="downlink-floating-label" htmlFor={`downlink-param-${key}`}>{key}</label>
          <input
            type={isInteger ? 'number' : 'text'}
            id={`downlink-param-${key}`}
            className={`downlink-input${error ? ' downlink-input--invalid' : ''}`}
            value={value}
            onChange={e => setValue(e.target.value)}
            onBlur={() => touch(`param-${key}`)}
            min={isInteger ? schema.minimum : undefined}
            max={isInteger ? schema.maximum : undefined}
            step={isInteger ? 1 : undefined}
            placeholder={hasRange ? `${schema.minimum} – ${schema.maximum}` : undefined}
            autoComplete="off"
            aria-invalid={!!error}
          />
        </div>
        {error && <FieldError>{paramErrors[key]}</FieldError>}
      </div>
    );
  };

  // Kam der Nutzer von der Detailseite, wird der Verlaufseintrag dieser Seite
  // wieder verlassen statt ein neuer angelegt – so führt „Zurück“ auf der
  // Detailseite anschließend dorthin, woher der Nutzer ursprünglich kam.
  const backToDetails = () => {
    if (location.state?.fromDetails) navigate(-1);
    else navigate(`/devices/${deviceId}`, { replace: true });
  };

  return (
    <div className="send-downlink">
      <div className="device-toolbar">
        <div className="device-toolbar-left">
          <button
            type="button"
            className="back-button"
            onClick={backToDetails}
            aria-label="Zurück zur Detailseite"
            title="Zurück zur Detailseite"
          >
            <span aria-hidden="true"><Zurück /></span>
          </button>
          <h1 className="device-title">{device?.name || 'Downlink senden'}</h1>
        </div>
      </div>

      <StatusBar message={statusMessage} statusType={statusType} />

      {loading && <p className="downlink-loading">Lade Downlink-Befehle …</p>}

      {!loading && loadError && (
        <div className="downlink-page-error" role="alert">{loadError}</div>
      )}

      {!loading && !loadError && (
        <form className="downlink-form" onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} noValidate>

          {/* Zeile 1: Command | Parameter */}
          <div className="downlink-grid-row">
            <div className="downlink-section">
              <SectionHeader
                name="command"
                number="❶"
                title="Befehl auswählen"
                required
                tooltip="Wähle den Befehl aus, der als Downlink an das Gerät gesendet werden soll. Zur Auswahl stehen alle Befehle, die für dieses Gerätemodell hinterlegt sind."
              />
              {commandOptions.length === 0 ? (
                <p className="downlink-hint">Für dieses Gerät sind keine Downlink-Befehle hinterlegt.</p>
              ) : (
                <SearchDropdown
                  id="downlink-command"
                  label="Befehl"
                  placeholder="Befehl wählen"
                  inputValue={commandText}
                  onInputChange={text => { setCommandText(text); selectCommand(''); }}
                  options={commandOptions}
                  onSelect={o => { selectCommand(o.value); setCommandText(o.label); }}
                  onClear={() => { selectCommand(''); setCommandText(''); }}
                  invalid={commandError}
                  onBlur={() => touch('command')}
                  focusNextOnSelect
                />
              )}
              {commandError && commandOptions.length > 0 && (
                <FieldError>
                  {commandText.trim() ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.' : 'Dieses Feld ist erforderlich.'}
                </FieldError>
              )}
            </div>

            {commandDef && (
              <div className="downlink-section">
                <SectionHeader
                  name="parameters"
                  number="❷"
                  title={selectedCommand}
                  required={requiredParams.length > 0}
                  tooltip={commandDef.description}
                />
                {paramEntries.length === 0
                  ? <p className="downlink-hint">Dieser Befehl benötigt keine weiteren Angaben.</p>
                  : paramEntries.map(([key, schema]) => renderParameterInput(key, schema))}
              </div>
            )}
          </div>

          {/* Zeile 2: Weitere Geräte? | Geräteauswahl */}
          {commandDef && (
            <div className="downlink-grid-row">
              <div className="downlink-section">
                <SectionHeader
                  name="apply-others"
                  number="❸"
                  title="An weitere Geräte senden?"
                  tooltip="Der Downlink kann zusätzlich an weitere Geräte desselben Herstellers und Modells gesendet werden."
                />
                <RadioGroup
                  name="apply-others"
                  labelledBy="downlink-label-apply-others"
                  options={APPLY_TO_OTHERS_OPTIONS}
                  value={applyToOthers}
                  onChange={changeApplyToOthers}
                />
              </div>

              {applyToOthers === 'YES' && (
                <div className="downlink-section">
                  <SectionHeader
                    name="devices"
                    number="❹"
                    title="Geräte auswählen"
                    required
                    tooltip={`Wähle die Geräte aus, an die der Downlink zusätzlich gesendet werden soll. Zur Auswahl stehen alle Geräte vom Typ ${device?.vendor || ''} ${device?.model || ''}.`}
                  />
                  {otherDevicesLoading && <p className="downlink-hint">Lade Geräte …</p>}
                  {otherDevicesError && <FieldError>{otherDevicesError}</FieldError>}
                  {!otherDevicesLoading && !otherDevicesError && (
                    otherDevices.length === 0 ? (
                      <p className="downlink-hint">Es gibt keine weiteren Geräte dieses Herstellers und Modells.</p>
                    ) : (
                      <>
                        <SearchDropdown
                          id="downlink-devices"
                          label="Gerät"
                          placeholder="Gerät suchen"
                          inputValue={deviceText}
                          onInputChange={setDeviceText}
                          options={deviceOptions}
                          onSelect={o => {
                            setSelectedDevices(prev => [...prev, { id: o.value, name: o.label }]);
                            setDeviceText('');
                          }}
                          onClear={() => setDeviceText('')}
                          disabled={allDevicesSelected}
                          invalid={devicesError}
                        />
                        {selectedDevices.length > 0 && !allDevicesSelected && (
                          <div className="selected-badges">
                            {selectedDevices.map(d => (
                              <span key={d.id} className="selected-badge">
                                {d.name}
                                <button
                                  type="button"
                                  className="badge-remove"
                                  aria-label={`Gerät ${d.name} entfernen`}
                                  title={`Gerät ${d.name} entfernen`}
                                  onClick={() => removeDevice(d.id)}
                                >✖</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <label className="downlink-checkbox-label">
                          <input
                            type="checkbox"
                            checked={allDevicesSelected}
                            onChange={e => handleAllDevices(e.target.checked)}
                          />
                          Alle Geräte wählen
                        </label>
                      </>
                    )
                  )}
                  {tooManyDevices && (
                    <FieldError>
                      Ein Downlink kann an höchstens {MAX_BULK_DEVICES} Geräte gleichzeitig gesendet werden (inklusive dieses Geräts). Aktuell ausgewählt: {targetDevices.length + 1}.
                    </FieldError>
                  )}
                  {devicesError && !otherDevicesError && (
                    <FieldError>Bitte wähle mindestens ein Gerät aus oder setze „Nein“ bei Feld ❸.</FieldError>
                  )}
                </div>
              )}
            </div>
          )}

          {!isValid && showErrors && (
            <p className="downlink-error-text" role="alert">Bitte fülle alle Pflichtfelder korrekt aus.</p>
          )}

          {submitError && <p className="downlink-error-text" role="alert">{submitError}</p>}

          <div className="downlink-required-explain" aria-hidden="true">
            <span className="downlink-required">*</span> Pflichtfeld
          </div>

          <div className="downlink-form-actions">
            <button type="submit" className="btn-primary" disabled={sending || commandOptions.length === 0}>
              <Downlink /> {sending ? 'Wird gesendet …' : 'Downlink senden'}
            </button>
            <button type="button" className="btn-secondary" onClick={backToDetails} disabled={sending}>
              <Abbrechen /> Abbrechen
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function SendDownlinkWithBoundary() {
  return <ErrorBoundary><SendDownlink /></ErrorBoundary>;
}
